import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import usptoApi from '../../../../api/data-sections/usptoApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { UsptoAssignmentModel } from '../../../../models/data-sections/uspto/assignmentModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import {
  handleError,
  mapResponseErrorToSystemError,
  mapUnknownErrorToSystemError,
} from '../../../../utils/errorUtils';

interface AssignmentModalProps {
  searchExecutionId: string;
  selected_assignment: UsptoAssignmentModel;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  searchExecutionId,
  selected_assignment,
}) => {
  const [assignmentData, setAssignmentData] = useState<UsptoAssignmentModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchAssignmentDetail(
          searchExecutionId,
          selected_assignment.reel_no || '',
          selected_assignment.frame_no || '',
          new Date((selected_assignment as any).__ci_system_search_date_key ?? 0)
        );
      } catch (error) {
        console.error('Error fetching assignment data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_assignment]);

  const fetchAssignmentDetail = async (
    search_execution_id: string,
    reel_no: string,
    frame_no: string,
    __ci_system_search_date_key: Date
  ) => {
    try {
      const response = await usptoApi.getAssignmentDetail(
        search_execution_id,
        frame_no,
        reel_no,
        __ci_system_search_date_key
      );

      if (response.success) {
        const data = (response as ResponseSuccessModel).data as UsptoAssignmentModel[];
        setAssignmentData(data?.[0] || null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          null,
          setDidErrorOccur,
          triggerSystemError
        );
      }
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        null,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1} mb={2}>
        <Typography variant="h6">
          {assignmentData?.invention_title || 'USPTO Assignment'}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          Reel/Frame: {selected_assignment.reel_no} / {selected_assignment.frame_no}
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : didErrorOccur || !assignmentData ? (
        <Typography color="error">Failed to load assignment details.</Typography>
      ) : (
        <Box mt={2}>
          <Typography><strong>Recorded Date:</strong> {assignmentData.recorded_date || 'N/A'}</Typography>
          <Typography><strong>Last Updated:</strong> {assignmentData.last_update_date || 'N/A'}</Typography>

          {assignmentData.assignees?.length ? (
            <Box mt={2}>
              <Typography><strong>Assignees:</strong></Typography>
              {assignmentData.assignees.map((a, index) => (
                <Typography key={index}>{a.name}, {a.city}, {a.country}</Typography>
              ))}
            </Box>
          ) : null}

          {assignmentData.assignors?.length ? (
            <Box mt={2}>
              <Typography><strong>Assignors:</strong></Typography>
              {assignmentData.assignors.map((a, index) => (
                <Typography key={index}>{a.name} (Executed: {a.execution_date})</Typography>
              ))}
            </Box>
          ) : null}

          {assignmentData.document_ids?.length ? (
            <Box mt={3}>
              <Typography variant="h6">Documents</Typography>
              <Box component="ul" pl={3}>
                {assignmentData.document_ids.map((doc, index) => (
                  <li key={index}>
                    {doc.country} {doc.doc_number} ({doc.kind}) - {doc.date}
                  </li>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
    </>
  );
};

export default AssignmentModal;
